import type { UIMessage } from "ai";

import { todosFrom, todosFromToolOutput } from "../plan/model";
import type { PlanStatus, PlanTodo } from "../types";
import type {
  WorkspaceSection,
  WorkspaceStatus
} from "../workspace/workspaceModel";

export type RunKind =
  | "draft"
  | "research"
  | "packet"
  | "export"
  | "form"
  | "autocategorize"
  | "pdf_edit"
  | "titling";

export type TrackedRun = {
  kind: RunKind;
  id: string;
  /** Tool that started it, for the card title. */
  toolName: string;
  /** Status as last reported by the tool output itself, when it carries one. */
  reportedStatus?: string | null;
};

type ToolLikePart = {
  type: string;
  toolCallId?: string;
  state?: string;
  toolName?: string;
  output?: unknown;
};

const RUN_KINDS = new Set<RunKind>([
  "draft",
  "research",
  "packet",
  "export",
  "form",
  "autocategorize",
  "pdf_edit",
  "titling"
]);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/**
 * Pull a long-running job out of a tool result. Tools report either
 * `run: {kind, id}` (drafting, packet, research, export, pdf_edit) or a full
 * autocategorize `run: {run_id, status, ...}`, or that compact run as the
 * whole result. A pdf_edit result also carries its status under `pdf_edit`,
 * which is the only place the IDE can read it from.
 */
export const runFromToolOutput = (
  toolName: string,
  output: unknown
): TrackedRun | null => {
  if (!isRecord(output) || output.error) return null;
  const { pdf_edit: detail } = output;
  const run = isRecord(output.run)
    ? output.run
    : toolName.includes("autocategorize")
    ? output
    : null;
  if (!run) return null;
  if (
    typeof run.kind === "string" &&
    RUN_KINDS.has(run.kind as RunKind) &&
    run.id != null
  ) {
    const reportedStatus =
      run.kind === "pdf_edit" &&
      isRecord(detail) &&
      typeof detail.status === "string"
        ? detail.status
        : undefined;
    return {
      kind: run.kind as RunKind,
      id: String(run.id),
      toolName,
      reportedStatus
    };
  }
  if (run.run_id != null) {
    return {
      kind: "autocategorize",
      id: String(run.run_id),
      toolName,
      reportedStatus: typeof run.status === "string" ? run.status : null
    };
  }
  return null;
};

const toolNameOf = (part: ToolLikePart): string =>
  part.type === "dynamic-tool"
    ? part.toolName ?? "tool"
    : part.type.replace(/^tool-/, "");

/** A background run's terminal status as reported by a system notice. */
export type RunEvent = { kind: string; id: string; status: string };

export const runKey = (run: { kind: string; id: string }): string =>
  `${run.kind}:${run.id}`;

type MessageLike = Pick<UIMessage, "role" | "parts"> & {
  metadata?: unknown;
};

/**
 * The system notice a message carries, if it is one. The backend posts these
 * (`author.kind === "system"`) when work the agent started finishes; `event`
 * names the run the same way the start tool did (`kind`, `id`).
 */
export const runEventOf = (message: MessageLike): RunEvent | null => {
  if (message.role !== "user" || !isRecord(message.metadata)) return null;
  const { author, event } = message.metadata;
  if (!isRecord(author) || author.kind !== "system") return null;
  if (
    !isRecord(event) ||
    typeof event.kind !== "string" ||
    !RUN_KINDS.has(event.kind as RunKind) ||
    event.id == null ||
    typeof event.status !== "string"
  )
    return null;
  return { kind: event.kind, id: String(event.id), status: event.status };
};

/** Latest status each run was reported at by a system notice, by `kind:id`. */
export const runEventsInMessages = (
  messages: readonly MessageLike[]
): Map<string, RunEvent> => {
  const events = new Map<string, RunEvent>();
  for (const m of messages) {
    const event = runEventOf(m);
    if (event) events.set(runKey(event), event);
  }
  return events;
};

/**
 * A plan build as the transcript tells it: the plan the Build message named,
 * with its todos as last reported. `todos` starts from the snapshot stamped on
 * the Build message and is replaced by each later `update_todos` result for
 * the plan, so the card shows progress live and reads the same after a reload.
 */
export type BuildProgress = {
  planId: string;
  title: string;
  todos: PlanTodo[];
  /** The plan's status as `update_todos` last reported it, if it did. */
  status: PlanStatus | null;
};

export type BuildProgressIndex = {
  /** By the id of the user message that started the build. */
  byMessage: Map<string, BuildProgress>;
  /** Plans with a Build card somewhere in the transcript. */
  planIds: Set<string>;
};

const EMPTY_BUILDS: BuildProgressIndex = {
  byMessage: new Map(),
  planIds: new Set()
};

/** What a user message's `metadata.build` says, if it starts a plan build. */
export const buildOf = (
  message: Pick<MessageLike, "role" | "metadata">
): { planId: string; title: string; todos: PlanTodo[] } | null => {
  if (message.role !== "user" || !isRecord(message.metadata)) return null;
  const { build } = message.metadata;
  if (!isRecord(build) || typeof build.plan_id !== "string" || !build.plan_id)
    return null;
  return {
    planId: build.plan_id,
    title: typeof build.title === "string" ? build.title : "Plan",
    todos: todosFrom(build.todos)
  };
};

const planStatusOf = (v: unknown): PlanStatus | null =>
  v === "draft" || v === "building" || v === "complete" ? v : null;

/**
 * Every plan build in the transcript, each at its latest known todo state.
 * A Build message opens a build for its plan (closing an earlier open one for
 * the same plan, so a second Build of the same plan gets its own card); an
 * `update_todos` result for that plan then updates the open build.
 */
export const buildProgressInMessages = (
  messages: readonly (MessageLike & { id: string })[]
): BuildProgressIndex => {
  if (!messages.length) return EMPTY_BUILDS;
  const byMessage = new Map<string, BuildProgress>();
  const planIds = new Set<string>();
  // The message id of the open build per plan.
  const open = new Map<string, string>();

  const startBuild = (
    messageId: string,
    build: NonNullable<ReturnType<typeof buildOf>>
  ) => {
    byMessage.set(messageId, {
      planId: build.planId,
      title: build.title,
      todos: build.todos,
      status: null
    });
    planIds.add(build.planId);
    open.set(build.planId, messageId);
  };

  const applyUpdate = (part: ToolLikePart) => {
    const isToolPart =
      part.type.startsWith("tool-") || part.type === "dynamic-tool";
    if (!isToolPart || part.state !== "output-available") return;
    if (toolNameOf(part) !== "update_todos") return;
    const result = todosFromToolOutput(part.output);
    if (!result) return;
    // An output without a plan id (older builds) goes to the only open
    // build, when there is exactly one.
    const messageId =
      result.planId !== null
        ? open.get(result.planId)
        : open.size === 1
        ? [...open.values()][0]
        : undefined;
    const current = messageId ? byMessage.get(messageId) : undefined;
    if (!messageId || !current) return;
    byMessage.set(messageId, {
      ...current,
      title: result.title ?? current.title,
      todos: result.todos,
      status: isRecord(part.output)
        ? planStatusOf(part.output.status) ?? current.status
        : current.status
    });
  };

  for (const m of messages) {
    const build = buildOf(m);
    if (build) startBuild(m.id, build);
    else if (m.role === "assistant")
      (m.parts as unknown as ToolLikePart[]).forEach(applyUpdate);
  }
  return { byMessage, planIds };
};

export type BuildStep = {
  /** The todo being worked (first in progress, else first pending). */
  current: PlanTodo | null;
  /** Its 1-based position; the total once nothing is left. */
  position: number;
  total: number;
  /** Every todo is completed or cancelled. */
  finished: boolean;
};

/** Where a build stands, the way Cursor's Build card reads it: `1/20`. */
export const buildStep = (todos: readonly PlanTodo[]): BuildStep => {
  const total = todos.length;
  const activeIndex = todos.findIndex((t) => t.status === "in_progress");
  const index =
    activeIndex >= 0
      ? activeIndex
      : todos.findIndex((t) => t.status === "pending");
  if (index < 0)
    return { current: null, position: total, total, finished: total > 0 };
  return { current: todos[index], position: index + 1, total, finished: false };
};

/**
 * The run as a notice last reported it. The card that started the run then
 * resolves from the notice even before the tree or its poll catches up.
 */
export const withRunEvent = (
  run: TrackedRun,
  event: RunEvent | null | undefined
): TrackedRun =>
  event && event.kind === run.kind && event.id === run.id
    ? { ...run, reportedStatus: event.status }
    : run;

/**
 * Every run started anywhere in the transcript, newest last, deduped by
 * kind+id, each at the status its latest system notice reported (if any).
 */
export const runsInMessages = (messages: UIMessage[]): TrackedRun[] => {
  const seen = new Map<string, TrackedRun>();
  for (const m of messages) {
    if (m.role === "assistant") {
      for (const part of m.parts as unknown as ToolLikePart[]) {
        const isToolPart =
          part.type.startsWith("tool-") || part.type === "dynamic-tool";
        if (isToolPart && part.state === "output-available") {
          const run = runFromToolOutput(toolNameOf(part), part.output);
          if (run) {
            const key = runKey(run);
            seen.delete(key);
            seen.set(key, run);
          }
        }
      }
    }
  }
  const events = runEventsInMessages(messages);
  return [...seen.values()].map((run) =>
    withRunEvent(run, events.get(runKey(run)))
  );
};

export type RunView = {
  run: TrackedRun;
  status: WorkspaceStatus;
  title: string;
  detail: string | null;
  /** What a running draft's agent last reported doing. */
  activity?: string | null;
  /** When a running draft's run started (epoch ms). */
  since?: number | null;
};

const SECTION_FOR_KIND: Partial<Record<RunKind, string[]>> = {
  draft: ["drafts", "exhibit-list"],
  research: ["research"],
  packet: ["packet"],
  form: ["forms"]
};

const KIND_LABEL: Record<RunKind, string> = {
  draft: "Drafting",
  research: "Researching",
  packet: "Assembling packet",
  export: "Exporting filing materials",
  form: "Filling form",
  autocategorize: "Organizing files",
  pdf_edit: "Editing PDF",
  titling: "Proposing exhibit titles"
};

export type ExportRecordLike = {
  status: "processing" | "ready" | "failed";
  error?: string | null;
  download_name?: string | null;
};

export type AutocategorizeRecordLike = {
  status: string;
  error?: string | null;
};

/**
 * Whether an autocategorize run still needs following. `start_autocategorize`
 * reports `running`; `get_autocategorize_plan` / `apply_autocategorize_plan`
 * report where the run ended up, so those cards need no poll.
 */
export const autocategorizeInFlight = (run: TrackedRun): boolean => {
  if (run.kind !== "autocategorize") return false;
  const s = (run.reportedStatus ?? "").toLowerCase();
  return s === "" || s === "running" || s === "applying";
};

/**
 * Resolve a run's live status from what the workspace tree already knows.
 * Exports and autocategorize runs are not in the tree, so their polled
 * records are passed in; a polled autocategorize status wins over the one
 * frozen in the tool output.
 */
export const runView = (
  run: TrackedRun,
  sections: WorkspaceSection[],
  exportRecord: ExportRecordLike | null = null,
  autocategorizeRecord: AutocategorizeRecordLike | null = null
): RunView => {
  if (run.kind === "export") {
    // The polled record wins; before it arrives a system notice's status will do.
    const reported = exportRecord?.status ?? run.reportedStatus ?? "";
    const status: WorkspaceStatus =
      reported === "ready"
        ? "ready"
        : reported === "failed"
        ? "failed"
        : "running";
    const detail =
      status === "ready"
        ? exportRecord?.download_name ?? "ZIP ready"
        : status === "failed"
        ? exportRecord?.error ?? "Export failed"
        : "Bundling the packet, exhibit list, drafts and forms…";
    return { run, status, title: KIND_LABEL.export, detail };
  }
  const sectionIds = SECTION_FOR_KIND[run.kind];
  if (sectionIds) {
    const sectionWithItem = sections
      .filter((s) => sectionIds.includes(s.id))
      .map((s) => s.items.find((i) => i.artifact.id === run.id) ?? null)
      .find((item) => item !== null);
    if (sectionWithItem) {
      return {
        run,
        status: sectionWithItem.status,
        title: `${KIND_LABEL[run.kind]}: ${sectionWithItem.artifact.title}`,
        detail: sectionWithItem.detail ?? null,
        activity: sectionWithItem.activity ?? null,
        since: sectionWithItem.runningSince ?? null
      };
    }
    // Not visible yet (or deleted): treat as running until the tree knows.
    return {
      run,
      status: "running",
      title: KIND_LABEL[run.kind],
      detail: null
    };
  }
  if (run.kind === "autocategorize") {
    const reported = autocategorizeRecord?.status ?? run.reportedStatus ?? "";
    const s = reported.toLowerCase();
    const status: WorkspaceStatus =
      s === "failed"
        ? "failed"
        : s === "applied"
        ? "ready"
        : s === "proposed"
        ? "stale"
        : s === "canceled"
        ? "idle"
        : "running";
    const detail =
      s === "proposed"
        ? "Plan ready for review"
        : s === "applied"
        ? "Applied"
        : s === "failed"
        ? autocategorizeRecord?.error ?? "Organizer failed"
        : reported || null;
    return { run, status, title: KIND_LABEL.autocategorize, detail };
  }
  if (run.kind === "pdf_edit") {
    const s = (run.reportedStatus ?? "").toLowerCase();
    const status: WorkspaceStatus =
      s === "failed"
        ? "failed"
        : s === "succeeded"
        ? "ready"
        : s === "canceled"
        ? "idle"
        : "running";
    const detail =
      s === "succeeded"
        ? "New version saved"
        : s === "failed"
        ? "Edit failed"
        : s === "canceled"
        ? "Canceled"
        : "Editing in the PDF sandbox…";
    return { run, status, title: KIND_LABEL.pdf_edit, detail };
  }
  if (run.kind === "titling") {
    // Only the wake-up notice reports on it; nothing to poll.
    const s = (run.reportedStatus ?? "").toLowerCase();
    const status: WorkspaceStatus =
      s === "failed" ? "failed" : s === "ready" ? "ready" : "running";
    const detail =
      status === "ready"
        ? "Titles proposed"
        : status === "failed"
        ? "Titling failed"
        : "Reading the files to propose titles…";
    return { run, status, title: KIND_LABEL.titling, detail };
  }
  return { run, status: "running", title: KIND_LABEL[run.kind], detail: null };
};
